import { path } from 'ramda';
import { generatePath } from 'react-router-dom';
import { UPLOAD_CONTENT_API_PREFIX } from '../constants/content';
import { formatAPIUrl } from './templater';

export const getDevice = (isDesktop = true) =>
  isDesktop ? 'desktop' : 'mobile';
export const getDRP = () =>
  window?.devicePixelRatio > 1.5 ? '_dpr_2' : '_dpr_1';

/**
 * Utility function to get image of game.
 * @param path {string} - path from common config
 * @param gameId {string}
 * @param isDesktop {boolean}
 * @returns {string}
 */
export const getGameImagePath = (path, gameId, isDesktop) =>
  generatePathOrEmptyString(path, {
    gameId,
    device: getDevice(isDesktop),
    dpr: getDRP(),
    n: ':n', // change :n to :n (specific of generatePath)
  });

export const getCategoryImagePath = (path, categoryId, isDesktop) =>
  generatePathOrEmptyString(path, {
    categoryId,
    device: getDevice(isDesktop),
    dpr: getDRP(),
    n: ':n', // change :n to :n (specific of generatePath)
  });

export const getGameProviderImagePath = (
  path,
  providerId = '',
  isDesktop = true
) =>
  generatePathOrEmptyString(path, {
    providerId,
    device: getDevice(isDesktop),
    n: ':n',
  });

export const isEntityHasExtension = (entity) => {
  return entity?.includes('.');
};

export const getEntityExtension = (entity) => {
  return isEntityHasExtension(entity) ? entity?.split('.')?.pop() : '';
};

// we need to give possibility set different file extensions for entityId,
// so here we choose what extension should be used: path's or entityId's
export const formatPathWithPrioritiziedExtension = (
  path,
  pathWithoutExtension,
  entityId
) => {
  const entityIdExtension = getEntityExtension(entityId);

  return entityIdExtension
    ? {
        path: pathWithoutExtension,
        entityId,
      }
    : { path, entityId };
};

// Slider navigation widget image path
export const getNavigationLogoImagePath = (
  path,
  pathWithoutExtension,
  entityId
) => {
  const { path: formattedPath, entityId: formattedEntityId } =
    formatPathWithPrioritiziedExtension(path, pathWithoutExtension, entityId);

  return generatePathOrEmptyString(formattedPath, {
    entityId: formattedEntityId,
  });
};

export function generatePathOrEmptyString(path, options) {
  try {
    return generatePath(formatAPIUrl(path), options);
  } catch (err) {
    return '';
  }
}

/**
 * @param {*} entity
 * @param {String} proxyPrefix
 * @param {String | undefined} name
 * @return {*}
 */
export const getEntityWithImageUrl = (entity, proxyPrefix, name = 'image') => {
  const imageHash = path([name, 'hash'], entity);
  const imageExt = path([name, 'ext'], entity);
  const image = imageHash && imageExt && `${imageHash}${imageExt}`;
  const imageUrl = `${proxyPrefix}${UPLOAD_CONTENT_API_PREFIX}${image}`;

  return {
    ...entity,
    [name]: imageUrl,
  };
};

export const getLCGameImagePath = (
  commonConfig,
  gameId = '',
  isDesktop = true
) =>
  generatePathOrEmptyString(commonConfig?.imageTemplates?.GAME_IMAGE, {
    gameId,
    device: getDevice(isDesktop),
    n: ':n', // change :n to :n (specific of generatePath)
  });

export const getIGGameImagePath = (
  commonConfig,
  gameId = '',
  isDesktop = true
) =>
  generatePathOrEmptyString(commonConfig?.imageTemplates?.IG_GAME_IMAGE, {
    gameId,
    device: getDevice(isDesktop),
    n: ':n', // change :n to :n (specific of generatePath)
  });

export const getTVGamesGameImagePath = (
  commonConfig,
  gameId = '',
  isDesktop = true
) =>
  generatePathOrEmptyString(commonConfig?.imageTemplates?.TVGAMES_GAME_IMAGE, {
    gameId,
    device: getDevice(isDesktop),
    n: ':n', // change :n to :n (specific of generatePath)
  });

export const getSlotsGameImagePath = (
  commonConfig,
  gameId = '',
  isDesktop = true
) =>
  generatePathOrEmptyString(commonConfig?.imageTemplates?.SLOTS_GAME_IMAGE, {
    gameId,
    device: getDevice(isDesktop),
    n: ':n', // change :n to :n (specific of generatePath)
  });

export const getVSGameImagePath = (
  commonConfig,
  gameId = '',
  isDesktop = true
) =>
  generatePathOrEmptyString(commonConfig?.imageTemplates?.VS_GAME_IMAGE, {
    gameId,
    device: getDevice(isDesktop),
    n: ':n', // change :n to :n (specific of generatePath)
  });

export const getBingoGameImagePath = (
  commonConfig,
  gameId = '',
  isDesktop = true
) =>
  generatePathOrEmptyString(commonConfig?.imageTemplates?.BINGO_GAME_IMAGE, {
    gameId,
    device: getDevice(isDesktop),
    n: ':n', // change :n to :n (specific of generatePath)
  });

export const isSvg = (fileName) => {
  return fileName?.endsWith('.svg');
};
